/**
 * @fileoverview Classifies browser-side errors for observability and recovery.
 * @description Maps raw `Error` instances to stable categories so the log drain
 * can filter transient deploy/network noise from actionable application faults,
 * and so auto-recovery can target only errors where a one-shot reload helps.
 *
 * @module lib/client-error-classification
 */

import {
  REACT_HYDRATION_ERROR_CODE_STRINGS,
  REACT_MINIFIED_ERROR_RE,
} from '~/lib/observability/client-errors'

/** Stable taxonomy for client errors in BetterStack filters and alerts. */
export type ClientErrorCategory =
  | 'chunk_load'
  | 'dom_reconciliation'
  | 'hydration'
  | 'application'

/**
 * Structured classification attached to every `client_error` log entry.
 */
export interface ClientErrorClassification {
  /** High-level bucket for drain filters and alert routing. */
  errorCategory: ClientErrorCategory
  /**
   * Whether a one-shot full reload is a reasonable recovery attempt
   * (version skew, transient chunk fetch, DOM tree mutated under React).
   */
  recoverable: boolean
  /**
   * When true, the failure is commonly caused by external DOM mutation
   * (browser translation, extensions) rather than application logic.
   */
  likelyExternalDomMutation?: boolean
  /** Parsed chunk URL from a ChunkLoadError message, when present. */
  chunkUrl?: string
  /** Deployment id from the chunk URL `?dpl=` query param, when present. */
  chunkDeploymentId?: string
  /** Turbopack/webpack module id from "from module N" in ChunkLoadError text. */
  chunkModuleId?: string
  /** React minified error code when the message matches the prod pattern. */
  reactErrorCode?: string
  /** True for React hydration-family error codes (418, 419, 421–423, 425). */
  isHydrationError?: boolean
}

const CHUNK_LOAD_ERROR_RE =
  /^(?:ChunkLoadError|Loading chunk \d+ failed|Failed to load chunk )/i

const CHUNK_URL_RE = /(\/_next\/static\/chunks\/[^\s"']+\.js(?:\?[^\s"']*)?)/i

const DPL_QUERY_RE = /[?&]dpl=([^&\s"']+)/

const CHUNK_MODULE_ID_RE = /from module (\d+)/i

// External actors reparenting nodes under React (Facebook in-app browser,
// browser translation, extensions) surface as NotFoundError /
// HierarchyRequestError DOMExceptions from React's commit-phase mutation
// methods (removeChild/insertBefore/appendChild/replaceChild). removeChild
// keeps its original any-reason match; the sibling methods are gated below on
// evidence of that DOMException class — the name (on the error or folded into
// the message) or a specific reconciliation reason clause — so an application
// TypeError like "Failed to execute 'appendChild' on 'Node': parameter 1 is
// not of type 'Node'" stays application/non-recoverable (r3598601988).
// insertBefore variant observed in prod 2026-07-14 (FB in-app browser burst).
const DOM_RECONCILIATION_RE =
  /^(?:(?:NotFoundError|HierarchyRequestError): )?(?:Failed to execute 'removeChild' on 'Node'|The object can not be found here\.)/i

// Sibling mutation methods require one of the known reconciliation reason
// clauses (each implies a NotFoundError/HierarchyRequestError DOMException).
const DOM_SIBLING_RECONCILIATION_RE =
  /^(?:(?:NotFoundError|HierarchyRequestError): )?Failed to execute '(?:insertBefore|appendChild|replaceChild)' on 'Node': (?:The new child element contains the parent|The node before which the new node is to be inserted is not a child of this node|The node to be replaced is not a child of this node)/i

// …or an explicit DOMException name on the error object itself, paired with
// the mutation-method message prefix.
const DOM_RECONCILIATION_ERROR_NAMES = new Set([
  'NotFoundError',
  'HierarchyRequestError',
])
const DOM_MUTATION_PREFIX_RE =
  /^Failed to execute '(?:removeChild|insertBefore|appendChild|replaceChild)' on 'Node'/i

const DOM_RECONCILIATION_STACK_RE = /removeChild@\[native code\]/i

// The hydration-family code set and the minified-error matcher are DERIVED
// from the single registry in `~/lib/observability/client-errors`
// (`REACT_ERROR_REGISTRY`) — add new codes there, never here.
const REACT_HYDRATION_ERROR_CODES = REACT_HYDRATION_ERROR_CODE_STRINGS

const MINIFIED_REACT_ERROR_RE = REACT_MINIFIED_ERROR_RE

/**
 * Whether the error is a JavaScript chunk load failure (deploy skew, CDN flake,
 * ad-blocker, or aborted navigation).
 *
 * @param error - Error to inspect.
 * @returns `true` when the error name or message indicates a chunk load failure.
 */
export function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    CHUNK_LOAD_ERROR_RE.test(error.message)
  )
}

/**
 * Whether the error is React DOM reconciliation failing because the DOM tree
 * no longer matches what React expects — a node to remove/insert has been
 * moved or reparented by an external actor (browser translation, in-app
 * browsers, extensions, some iOS WebKit edge cases).
 *
 * @param error - Error to inspect.
 * @returns `true` for removeChild/insertBefore/appendChild/replaceChild
 * NotFoundError / HierarchyRequestError reconciliation failures.
 */
export function isDomReconciliationError(error: Error): boolean {
  if (DOM_RECONCILIATION_RE.test(error.message)) {
    return true
  }
  if (DOM_SIBLING_RECONCILIATION_RE.test(error.message)) {
    return true
  }
  if (
    DOM_RECONCILIATION_ERROR_NAMES.has(error.name) &&
    DOM_MUTATION_PREFIX_RE.test(error.message)
  ) {
    return true
  }
  // Stack-only signal is accepted only for NotFoundError — avoids misclassifying
  // unrelated faults that merely transit removeChild in the stack.
  if (
    error.name === 'NotFoundError' &&
    error.stack !== undefined &&
    DOM_RECONCILIATION_STACK_RE.test(error.stack)
  ) {
    return true
  }
  return false
}

/**
 * Extracts the hashed chunk path from a ChunkLoadError message.
 *
 * @param message - Raw error message.
 * @returns Chunk URL path when parseable.
 */
export function extractChunkUrlFromMessage(message: string): string | undefined {
  return CHUNK_URL_RE.exec(message)?.[1]
}

/**
 * Reads the deployment id embedded in a chunk URL's `?dpl=` query param.
 *
 * @param chunkUrl - Chunk path or full URL from an error message.
 * @returns Deployment id when present.
 */
export function extractDeploymentIdFromChunkUrl(
  chunkUrl: string,
): string | undefined {
  return DPL_QUERY_RE.exec(chunkUrl)?.[1]
}

/**
 * Extracts the bundler module id from a ChunkLoadError message.
 *
 * @param message - Raw error message.
 * @returns Module id when parseable (e.g. `474752` in production logs).
 */
export function extractChunkModuleIdFromMessage(message: string): string | undefined {
  return CHUNK_MODULE_ID_RE.exec(message)?.[1]
}

/**
 * Classify a client-side error for structured logging and recovery decisions.
 *
 * @param error - The thrown error (or Error-shaped rejection).
 * @returns Classification fields merged into the `/api/log` context payload.
 */
export function classifyClientError(error: Error): ClientErrorClassification {
  const reactCode = MINIFIED_REACT_ERROR_RE.exec(error.message)?.[1]
  const isHydrationError =
    reactCode !== undefined && REACT_HYDRATION_ERROR_CODES.has(reactCode)

  if (isChunkLoadError(error)) {
    const chunkUrl = extractChunkUrlFromMessage(error.message)
    const chunkModuleId = extractChunkModuleIdFromMessage(error.message)
    return {
      errorCategory: 'chunk_load',
      recoverable: true,
      ...(chunkUrl
        ? {
            chunkUrl,
            chunkDeploymentId: extractDeploymentIdFromChunkUrl(chunkUrl),
          }
        : {}),
      ...(chunkModuleId ? { chunkModuleId } : {}),
      ...(reactCode ? { reactErrorCode: reactCode, isHydrationError } : {}),
    }
  }

  if (isDomReconciliationError(error)) {
    return {
      errorCategory: 'dom_reconciliation',
      recoverable: true,
      likelyExternalDomMutation: true,
      ...(reactCode ? { reactErrorCode: reactCode, isHydrationError } : {}),
    }
  }

  if (isHydrationError) {
    return {
      errorCategory: 'hydration',
      recoverable: false,
      reactErrorCode: reactCode,
      isHydrationError: true,
    }
  }

  return {
    errorCategory: 'application',
    recoverable: false,
    ...(reactCode ? { reactErrorCode: reactCode } : {}),
  }
}
